import { useRef, useEffect, useState } from 'react';

// Customer subdomain from env or default
const STREAM_CUSTOMER_SUBDOMAIN = import.meta.env.VITE_CLOUDFLARE_STREAM_SUBDOMAIN || 'customer-tcnvhg316zo7rj0i';

/**
 * Hook to detect potential screen capture/recording
 */
function useScreenCaptureDetection() {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    // Detect visibility changes (tab switching, minimizing)
    const handleVisibilityChange = () => {
      setIsHidden(document.hidden);
    };

    // Detect window blur (potential screen sharing indicator)
    const handleBlur = () => {
      // Only flag as potential capture if focus lost for extended time
      // This helps avoid false positives from brief focus changes
    };

    // Intercept getDisplayMedia to detect screen sharing attempts
    const originalGetDisplayMedia = navigator.mediaDevices?.getDisplayMedia;
    if (navigator.mediaDevices && originalGetDisplayMedia) {
      navigator.mediaDevices.getDisplayMedia = async function(constraints) {
        setIsCapturing(true);
        // Still allow the call but we've detected it
        return originalGetDisplayMedia.call(navigator.mediaDevices, constraints);
      };
    }

    // Detect Picture-in-Picture (could be used for capture)
    const handlePiP = () => {
      if (document.pictureInPictureElement) {
        setIsCapturing(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('enterpictureinpicture', handlePiP);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('enterpictureinpicture', handlePiP);
      window.removeEventListener('blur', handleBlur);
      
      // Restore original getDisplayMedia
      if (navigator.mediaDevices && originalGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = originalGetDisplayMedia;
      }
    };
  }, []);

  return { isCapturing, isHidden };
}

interface CloudflareStreamPlayerProps {
  /** The Cloudflare Stream video ID or signed URL */
  videoId: string;
  /** Optional poster/thumbnail image */
  poster?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to autoplay (muted required for autoplay) */
  autoplay?: boolean;
  /** Whether to loop the video */
  loop?: boolean;
  /** Whether to mute the video */
  muted?: boolean;
  /** Whether to preload the video */
  preload?: 'auto' | 'metadata' | 'none';
  /** Primary color for player controls (hex without #) */
  primaryColor?: string;
  /** Letter identifier for A/B testing */
  letterboxColor?: string;
  /** Start time in seconds */
  startTime?: number;
  /** Signed token for private videos */
  signedToken?: string;
  /** Callback when video starts playing */
  onPlay?: () => void;
  /** Callback when video ends */
  onEnded?: () => void;
  /** Callback for progress updates (at 25%, 50%, 75% milestones) */
  onProgress?: (progress: { currentTime: number; duration: number; percent: number }) => void;
}

/**
 * Cloudflare Stream Player component with DRM support.
 * 
 * This uses Cloudflare's Stream Player which has built-in support for:
 * - Widevine DRM (Chrome, Firefox, Edge, Android)
 * - FairPlay DRM (Safari, iOS)
 * - Hardware-level video encryption
 * 
 * For DRM to work, you need to:
 * 1. Enable "Require signed URLs" on your Cloudflare Stream settings
 * 2. Generate signed tokens server-side
 * 3. Pass the signedToken prop
 * 
 * Note: Buffering control is limited since this uses an iframe.
 * Use the `preload` prop to control initial loading behavior.
 * For fine-grained buffering control, consider using Mux instead.
 * 
 * @see https://developers.cloudflare.com/stream/viewing-videos/securing-your-stream/
 */
export function CloudflareStreamPlayer({
  videoId,
  poster,
  className = '',
  autoplay = false,
  loop = false,
  muted = false,
  preload = 'metadata',
  primaryColor,
  letterboxColor,
  startTime,
  signedToken,
  onPlay,
  onEnded,
  onProgress,
}: CloudflareStreamPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasPlayedRef = useRef(false);
  const lastProgressMilestoneRef = useRef(0);
  
  // Screen capture detection
  const { isCapturing, isHidden } = useScreenCaptureDetection();
  const showWarning = isCapturing || isHidden;

  // Determine if videoId is already a full signed URL or just an ID
  const isSignedUrl = videoId.includes('token=') || videoId.startsWith('https://');
  
  // Build the iframe src URL
  const buildSrc = () => {
    let src: string;
    
    if (isSignedUrl) {
      src = videoId.startsWith('https://') ? videoId : `https://${STREAM_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${videoId}/iframe`;
    } else if (signedToken) {
      src = `https://${STREAM_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${signedToken}/iframe`;
    } else {
      src = `https://${STREAM_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${videoId}/iframe`;
    }

    const params = new URLSearchParams();
    if (autoplay) params.set('autoplay', 'true');
    if (loop) params.set('loop', 'true');
    if (muted) params.set('muted', 'true');
    if (preload) params.set('preload', preload);
    if (primaryColor) params.set('primaryColor', primaryColor);
    if (letterboxColor) params.set('letterboxColor', letterboxColor);
    if (startTime) params.set('startTime', startTime.toString());
    if (poster) params.set('poster', poster);

    const queryString = params.toString();
    return queryString ? `${src}?${queryString}` : src;
  };

  // Listen to Stream Player events via postMessage
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from Cloudflare Stream
      if (!event.origin.includes('cloudflarestream.com')) return;
      
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Handle different event formats from Cloudflare Stream
        const eventType = data.event || data.type || data.name;
        
        if ((eventType === 'play' || eventType === 'playing') && !hasPlayedRef.current) {
          hasPlayedRef.current = true;
          onPlay?.();
        } else if (eventType === 'ended' || eventType === 'end') {
          onEnded?.();
        } else if (eventType === 'timeupdate' && onProgress) {
          // Handle progress tracking at milestones
          const { currentTime, duration } = data;
          if (duration > 0) {
            const percent = Math.round((currentTime / duration) * 100);
            const milestones = [25, 50, 75];
            for (const milestone of milestones) {
              if (percent >= milestone && lastProgressMilestoneRef.current < milestone) {
                lastProgressMilestoneRef.current = milestone;
                onProgress({ currentTime, duration, percent: milestone });
              }
            }
          }
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onPlay, onEnded, onProgress]);
  
  // Reset refs when videoId changes
  useEffect(() => {
    hasPlayedRef.current = false;
    lastProgressMilestoneRef.current = 0;
  }, [videoId]);

  // Prevent right-click on the video container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    container.addEventListener('contextmenu', handleContextMenu);
    return () => container.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`}
      data-protected
      style={{ WebkitTouchCallout: 'none' }}
    >
      {/* Video iframe */}
      <iframe
        ref={iframeRef}
        src={buildSrc()}
        loading="lazy"
        style={{
          border: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          // Blur video when capture detected
          filter: showWarning ? 'blur(20px)' : 'none',
          transition: 'filter 0.3s ease',
        }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media;"
        allowFullScreen
        title="Course Video"
      />
      
      {/* Screen capture warning overlay */}
      {showWarning && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            color: 'white',
            textAlign: 'center',
            padding: '20px',
          }}
        >
          <svg 
            width="64" 
            height="64" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            style={{ marginBottom: '16px', color: '#ef4444' }}
          >
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '8px' }}>
            {isCapturing ? 'Screen Recording Detected' : 'Video Paused'}
          </h3>
          <p style={{ fontSize: '0.875rem', opacity: 0.8, maxWidth: '300px' }}>
            {isCapturing 
              ? 'Screen recording or sharing is not allowed for this content.'
              : 'Please return to this tab to continue watching.'
            }
          </p>
        </div>
      )}
    </div>
  );
}

